import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    ToggleButton,
    ToggleButtonGroup,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    CircularProgress,
    Alert,
    TextField,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useTheme, type Theme } from '@mui/material/styles';
import { useNotify } from 'react-admin';
import {
    Employee,
    getTimesheets,
    resetTimesheets,
    saveTimesheets,
    TIMESHEET_CODES,
    TimesheetCode,
    TimesheetEntry,
    TimesheetsResponse,
} from '../dataProvider';
import { ConfirmOldEditDialog } from './ConfirmOldEditDialog';
import { normalizeVietnamese } from '../utils/normalizeVietnamese';

/** View mode for the grid: a single Monday-Sunday week, or a full calendar month. */
type GridMode = 'week' | 'month';

/**
 * Formats a `Date` as an ISO `YYYY-MM-DD` string using its local calendar
 * fields (no UTC conversion), matching how the date pickers/inputs work.
 *
 * @param d - The date to format.
 * @returns The date formatted as `YYYY-MM-DD`.
 */
function toISODate (d: Date): string {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

/**
 * Parses a `YYYY-MM-DD` string into a local `Date` at midnight.
 *
 * @param dateStr - The ISO date string to parse.
 * @returns The parsed `Date`.
 */
function parseISODate (dateStr: string): Date {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
}

/**
 * Returns every date string (YYYY-MM-DD) between `start` and `end`, inclusive.
 *
 * @param start - Start date (YYYY-MM-DD).
 * @param end   - End date (YYYY-MM-DD).
 * @returns Array of date strings covering the range.
 */
function enumerateDays (start: string, end: string): string[] {
    const days: string[] = [];
    let cur = parseISODate(start);
    const last = parseISODate(end);
    while (cur <= last) {
        days.push(toISODate(cur));
        cur = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate() + 1);
    }
    return days;
}

/**
 * Shifts an anchor date by one period (week or month) in the given direction.
 *
 * @param anchor    - Current anchor date (YYYY-MM-DD).
 * @param mode       - `'week'` shifts by 7 days; `'month'` shifts by 1 calendar month.
 * @param direction - `-1` for previous, `1` for next.
 * @returns The shifted anchor date (YYYY-MM-DD).
 */
function shiftAnchor (anchor: string, mode: GridMode, direction: 1 | -1): string {
    const d = parseISODate(anchor);
    if (mode === 'week') {
        return toISODate(new Date(d.getFullYear(), d.getMonth(), d.getDate() + 7 * direction));
    }
    return toISODate(new Date(d.getFullYear(), d.getMonth() + direction, d.getDate()));
}

/** Abbreviated weekday names indexed by `Date#getDay()` (0 = Sunday .. 6 = Saturday). */
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

/**
 * Returns the abbreviated weekday name (e.g. `"Mon"`) for a `YYYY-MM-DD` date
 * string, using local calendar fields so it always matches the date shown in
 * the same column.
 *
 * @param dateStr - The date to name (YYYY-MM-DD).
 * @returns The abbreviated weekday name.
 */
function getDayName (dateStr: string): string {
    return DAY_NAMES[parseISODate(dateStr).getDay()];
}

/**
 * Checks whether a `YYYY-MM-DD` date string falls on a Saturday or Sunday
 * (local calendar), for weekend column coloring.
 *
 * @param dateStr - The date to check.
 * @returns `true` when the date is a Saturday or Sunday.
 */
function isWeekend (dateStr: string): boolean {
    const day = parseISODate(dateStr).getDay();
    return day === 0 || day === 6;
}

/**
 * Human-readable description of each {@link TimesheetCode}, mirroring the
 * "Bảng chấm công" template. Used by the grid legend.
 */
const CODE_LABELS: Record<TimesheetCode, string> = {
    D: 'Full day',
    'D/2': 'Half day',
    P: 'Paid leave',
    'P/2': 'Half-day paid leave',
    KL: 'Unpaid leave',
    NB: 'Comp leave',
    NT: 'Weekly rest',
    KP: 'Absent, unauthorised',
};

/**
 * Light/dark background pair for each {@link TimesheetCode} other than `D` (a
 * normal full day worked, which is left uncolored so exceptions stand out).
 * The `dark` values are saturated (not just a darkened `light` hex) so they
 * pair correctly with a fixed white text color instead of a near-white
 * `text.primary` — the light hex values previously doubled as the
 * dark-theme background too, which put near-white theme text on a
 * near-white swatch. Used for both the grid cells and the legend swatches
 * via {@link getCodeColorSx}.
 */
const CODE_COLOR_TOKENS: Partial<Record<TimesheetCode, { light: string; dark: string }>> = {
    'D/2': { light: '#e3f2fd', dark: '#0d47a1' },
    P: { light: '#e8f5e9', dark: '#1b5e20' },
    'P/2': { light: '#f1f8e9', dark: '#33691e' },
    KL: { light: '#fff3e0', dark: '#e65100' },
    NB: { light: '#f3e5f5', dark: '#4a148c' },
    NT: { light: '#eceff1', dark: '#455a64' },
    KP: { light: '#ffebee', dark: '#b71c1c' },
};

/**
 * Resolves the theme-aware background + paired text color for a
 * {@link TimesheetCode} swatch/cell, so text stays legible under both the
 * light and dark MUI palettes. Returns `undefined` for `D` (and any code with
 * no token), which callers should treat as "leave uncolored".
 *
 * @param code - The attendance code to resolve a color for.
 * @param mode - The active MUI palette mode (`theme.palette.mode`).
 * @returns The background/text color pair, or `undefined` when uncolored.
 */
function getCodeColorSx (code: TimesheetCode, mode: 'light' | 'dark'): { backgroundColor: string; color: string } | undefined {
    const token = CODE_COLOR_TOKENS[code];
    if (!token) return undefined;
    return mode === 'dark'
        ? { backgroundColor: token.dark, color: '#fff' }
        : { backgroundColor: token.light, color: 'rgba(0, 0, 0, 0.87)' };
}

/**
 * Background for a day column that is a weekend or public holiday,
 * independent of any code-based cell coloring — a column stays colored even
 * after HR overrides individual cells with different codes. Holidays take
 * visual priority over weekends (a holiday that lands on a weekday is still
 * colored as a holiday).
 *
 * When the column is also `today`, this background is kept (not replaced)
 * and the caller layers a primary-colored outline on top via
 * {@link getDayColumnSx}, so both signals stay visible on the same column.
 *
 * @param theme - The active MUI theme.
 * @param isHoliday - Whether this column's date is a public holiday.
 * @param isWeekendDay - Whether this column's date is a Saturday or Sunday.
 * @returns The background color, or `undefined` for an ordinary weekday.
 */
function getDayColumnBackground (theme: Theme, isHoliday: boolean, isWeekendDay: boolean): string | undefined {
    if (isHoliday) {
        return theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.32)' : '#ffcdd2';
    }
    if (isWeekendDay) {
        return theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.09)' : '#eeeeee';
    }
    return undefined;
}

/**
 * Computes the full `sx` object for one day column (header or body cell):
 * the weekend/holiday background from {@link getDayColumnBackground}, plus —
 * for `today` — a primary-colored outline and bold weight layered on top
 * (rather than replacing the background), so a weekend/holiday column that
 * is also today keeps both signals visible.
 *
 * @param theme - The active MUI theme.
 * @param day - The column's date (YYYY-MM-DD).
 * @param todayStr - Today's date (YYYY-MM-DD).
 * @param holidays - Every public holiday date (YYYY-MM-DD) in the current range.
 * @returns An `sx`-compatible style object.
 */
function getDayColumnSx (theme: Theme, day: string, todayStr: string, holidays: ReadonlySet<string>): Record<string, unknown> {
    const isToday = day === todayStr;
    const isHoliday = holidays.has(day);
    const isWeekendDay = isWeekend(day);
    const background = getDayColumnBackground(theme, isHoliday, isWeekendDay);

    const sx: Record<string, unknown> = {};
    if (background) sx.backgroundColor = background;

    if (isToday) {
        if (!background) sx.backgroundColor = 'action.selected';
        sx.outline = `2px solid ${theme.palette.primary.main}`;
        sx.outlineOffset = '-2px';
        sx.fontWeight = 'bold';
    }

    return sx;
}

/**
 * Checks whether a `YYYY-MM-DD` date string is more than 7 days in the past,
 * relative to today (local time). Mirrors the backend's UTC-based check
 * closely enough for the UI's pre-emptive confirmation prompt — the backend
 * remains the source of truth and re-validates on every save.
 *
 * Known divergence: for a user in a timezone ahead of UTC (e.g. the
 * company's ICT/UTC+7) between local midnight and the UTC offset boundary,
 * this local-time check can disagree with the backend's UTC "today" by one
 * day. The client is always the *stricter* of the two in that window, so the
 * worst case is an unnecessary confirmation dialog — never a save the
 * backend would have rejected.
 *
 * @param dateStr - The date string to check.
 * @returns `true` when the date is strictly more than 7 days before today.
 */
function isOlderThanSevenDays (dateStr: string): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = parseISODate(dateStr);
    const diffDays = Math.round((today.getTime() - target.getTime()) / 86400000);
    return diffDays > 7;
}

/**
 * Re-exported from the shared `utils/normalizeVietnamese` module so existing
 * imports of `normalizeVietnamese` from this file (including tests) keep
 * working. Used for the employee quick-jump search as a client-side match
 * key; the raw (accented) `full_name` is still what's displayed. Filtering is
 * client-side only — the full employee list for a given period is already
 * fetched by `GET /timesheets`, so no backend `search` query parameter exists.
 */
export { normalizeVietnamese };

/** A pending single-cell edit awaiting the "older than 7 days" confirmation dialog. */
interface PendingEdit {
    employeeId: string;
    workDate: string;
    code: TimesheetCode;
}

/** A pending single-cell override reset awaiting the "older than 7 days" confirmation dialog. */
interface PendingReset {
    employeeId: string;
    workDate: string;
}

/**
 * TimesheetGrid – week/month attendance grid for HR admins.
 *
 * Rows are active employees, columns are calendar days in the selected
 * range. Today's column is visually highlighted in both modes. Editing a
 * cell whose date is older than 7 days opens {@link ConfirmOldEditDialog}
 * requiring the admin to type "confirm" before the save is retried with
 * `confirmation: "confirm"`.
 *
 * Requires the `timesheets.manage` permission; the parent route should gate
 * access accordingly.
 */
export const TimesheetGrid: React.FC = () => {
    const notify = useNotify();
    const theme = useTheme();
    const [mode, setMode] = useState<GridMode>('week');
    const [anchor, setAnchor] = useState<string>(() => toISODate(new Date()));
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<TimesheetsResponse | null>(null);
    const [pendingEdit, setPendingEdit] = useState<PendingEdit | null>(null);
    const [pendingReset, setPendingReset] = useState<PendingReset | null>(null);
    const [search, setSearch] = useState('');
    const [departmentFilter, setDepartmentFilter] = useState('');

    const todayStr = useMemo(() => toISODate(new Date()), []);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await getTimesheets(mode, anchor);
            setData(result);
        } catch (err: unknown) {
            const msg = (err as { message?: string })?.message || 'Failed to load timesheets';
            setError(msg);
            notify(msg, { type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [mode, anchor, notify]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const days = useMemo(
        () => (data ? enumerateDays(data.start, data.end) : []),
        [data]
    );

    const entryMap = useMemo(() => {
        const map = new Map<string, TimesheetEntry>();
        (data?.entries ?? []).forEach((e) => {
            map.set(`${e.employee_id}|${e.work_date}`, e);
        });
        return map;
    }, [data]);

    /** Public holiday dates (YYYY-MM-DD) in the current range, for weekend/holiday column coloring. */
    const holidaySet = useMemo(() => new Set(data?.holidays ?? []), [data]);

    /**
     * Persists a single cell edit, either directly (recent dates) or after
     * the admin confirms via {@link ConfirmOldEditDialog} (dates older than
     * 7 days).
     *
     * The saved row returned by `PUT /timesheets` is patched directly into
     * local state rather than triggering a full `fetchData()` refetch, so a
     * single-cell edit doesn't re-download and re-render the whole grid.
     *
     * @param employeeId  - The employee whose entry is being edited.
     * @param workDate    - The date of the entry (YYYY-MM-DD).
     * @param code        - The newly selected attendance code.
     * @param confirmation - `"confirm"` when re-submitting after the dialog.
     */
    const persistEdit = useCallback(async (
        employeeId: string,
        workDate: string,
        code: TimesheetCode,
        confirmation?: string
    ) => {
        try {
            const saved = await saveTimesheets([{ employeeId, workDate, code }], confirmation);
            notify('Timesheet entry saved', { type: 'success' });
            setPendingEdit(null);
            setData((prev) => {
                if (!prev) return prev;
                const nextEntries = [...prev.entries];
                saved.forEach((savedRow) => {
                    const idx = nextEntries.findIndex(
                        (e) => e.employee_id === savedRow.employee_id && e.work_date === savedRow.work_date
                    );
                    if (idx >= 0) {
                        nextEntries[idx] = savedRow;
                    } else {
                        nextEntries.push(savedRow);
                    }
                });
                return { ...prev, entries: nextEntries };
            });
        } catch (err: unknown) {
            const msg = (err as { message?: string })?.message || 'Failed to save timesheet entry';
            notify(msg, { type: 'error' });
        }
    }, [notify]);

    const handleCellChange = useCallback((employeeId: string, workDate: string, code: TimesheetCode) => {
        if (isOlderThanSevenDays(workDate)) {
            setPendingEdit({ employeeId, workDate, code });
            return;
        }
        persistEdit(employeeId, workDate, code);
    }, [persistEdit]);

    /**
     * Resets a single cell's HR-saved override, either directly (recent
     * dates) or after the admin confirms via {@link ConfirmOldEditDialog}
     * (dates older than 7 days) — the reset counterpart of {@link
     * persistEdit}.
     *
     * The recomputed default row returned by `DELETE /timesheets` is patched
     * directly into local state, same as a save, so the cell immediately
     * shows its generated default without a full `fetchData()` refetch.
     *
     * @param employeeId  - The employee whose override is being reset.
     * @param workDate    - The date of the entry (YYYY-MM-DD).
     * @param confirmation - `"confirm"` when re-submitting after the dialog.
     */
    const persistReset = useCallback(async (
        employeeId: string,
        workDate: string,
        confirmation?: string
    ) => {
        try {
            const reset = await resetTimesheets([{ employeeId, workDate }], confirmation);
            notify('Timesheet override reset', { type: 'success' });
            setPendingReset(null);
            setData((prev) => {
                if (!prev) return prev;
                const nextEntries = [...prev.entries];
                reset.forEach((resetRow) => {
                    const idx = nextEntries.findIndex(
                        (e) => e.employee_id === resetRow.employee_id && e.work_date === resetRow.work_date
                    );
                    if (idx >= 0) {
                        nextEntries[idx] = resetRow;
                    } else {
                        nextEntries.push(resetRow);
                    }
                });
                return { ...prev, entries: nextEntries };
            });
        } catch (err: unknown) {
            const msg = (err as { message?: string })?.message || 'Failed to reset timesheet entry';
            notify(msg, { type: 'error' });
        }
    }, [notify]);

    const handleCellReset = useCallback((employeeId: string, workDate: string) => {
        if (isOlderThanSevenDays(workDate)) {
            setPendingReset({ employeeId, workDate });
            return;
        }
        persistReset(employeeId, workDate);
    }, [persistReset]);

    const employees: Employee[] = data?.employees ?? [];

    /** Distinct, sorted department values present in the current employee list, for the department filter. */
    const departments = useMemo(() => {
        const set = new Set<string>();
        employees.forEach((emp) => {
            if (emp.department) set.add(emp.department);
        });
        return Array.from(set).sort((a, b) => a.localeCompare(b));
    }, [employees]);

    /**
     * Employees matching both the department filter and the quick-jump search
     * box, the latter using accent-insensitive substring matching against
     * `full_name` (see {@link normalizeVietnamese}). Empty filters match everyone.
     */
    const filteredEmployees = useMemo(() => {
        const query = normalizeVietnamese(search.trim());
        return employees.filter((emp) => {
            if (departmentFilter && emp.department !== departmentFilter) return false;
            if (!query) return true;
            return normalizeVietnamese(emp.full_name).includes(query);
        });
    }, [employees, search, departmentFilter]);

    return (
        <Card>
            <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
                    <Typography variant="h5">Timesheets</Typography>
                    <Box display="flex" alignItems="center" gap={2}>
                        <ToggleButtonGroup
                            value={mode}
                            exclusive
                            size="small"
                            onChange={(_e, value: GridMode | null) => {
                                if (value) setMode(value);
                            }}
                            aria-label="view mode"
                        >
                            <ToggleButton value="week" aria-label="week view">Week</ToggleButton>
                            <ToggleButton value="month" aria-label="month view">Month</ToggleButton>
                        </ToggleButtonGroup>
                        <Box display="flex" alignItems="center">
                            <IconButton
                                aria-label="previous period"
                                size="small"
                                onClick={() => setAnchor((prev) => shiftAnchor(prev, mode, -1))}
                            >
                                <ChevronLeftIcon />
                            </IconButton>
                            <Typography variant="body2" sx={{ minWidth: 120, textAlign: 'center' }}>
                                {data ? `${data.start} — ${data.end}` : ''}
                            </Typography>
                            <IconButton
                                aria-label="next period"
                                size="small"
                                onClick={() => setAnchor((prev) => shiftAnchor(prev, mode, 1))}
                            >
                                <ChevronRightIcon />
                            </IconButton>
                        </Box>
                    </Box>
                </Box>

                {/* Filter row, styled to match react-admin's <FilterForm> on the
                    other list pages (e.g. /users): filled, small, dense inputs
                    laid out inline and bottom-aligned. `fullWidth={false}` is
                    required on both controls — react-admin's default theme sets
                    `fullWidth: true` on MuiTextField/MuiFormControl, which would
                    otherwise make the search box eat the whole row and push the
                    department filter onto the next line. */}
                <Box display="flex" flexWrap="wrap" alignItems="flex-end" gap={2} sx={{ mb: 2 }}>
                    <TextField
                        label="Search employees"
                        placeholder="Search by name (accents optional)"
                        size="small"
                        fullWidth={false}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        inputProps={{ 'aria-label': 'Search employees by name' }}
                        sx={{ width: 280 }}
                    />
                    <FormControl size="small" fullWidth={false} sx={{ width: 200 }}>
                        <InputLabel id="timesheet-department-filter-label" shrink>Department</InputLabel>
                        <Select
                            labelId="timesheet-department-filter-label"
                            label="Department"
                            size="small"
                            displayEmpty
                            value={departmentFilter}
                            onChange={(e) => setDepartmentFilter(e.target.value)}
                            inputProps={{ 'aria-label': 'Filter by department' }}
                        >
                            <MenuItem value="">All departments</MenuItem>
                            {departments.map((dept) => (
                                <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Box display="flex" flexWrap="wrap" alignItems="center" gap={2} sx={{ mb: 2 }} aria-label="Legend">
                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Legend:</Typography>
                    {TIMESHEET_CODES.map((code) => (
                        <Box key={code} display="flex" alignItems="center" gap={0.5}>
                            <Box
                                data-testid={`legend-swatch-code-${code}`}
                                sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: 0.5,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    backgroundColor: getCodeColorSx(code, theme.palette.mode)?.backgroundColor ?? 'transparent',
                                }}
                            />
                            <Typography variant="caption">{code} — {CODE_LABELS[code]}</Typography>
                        </Box>
                    ))}
                    <Box display="flex" alignItems="center" gap={0.5}>
                        <Box
                            data-testid="legend-swatch-weekend"
                            sx={{
                                width: 12,
                                height: 12,
                                borderRadius: 0.5,
                                border: '1px solid',
                                borderColor: 'divider',
                                backgroundColor: getDayColumnBackground(theme, false, true),
                            }}
                        />
                        <Typography variant="caption">Weekend</Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.5}>
                        <Box
                            data-testid="legend-swatch-holiday"
                            sx={{
                                width: 12,
                                height: 12,
                                borderRadius: 0.5,
                                border: '1px solid',
                                borderColor: 'divider',
                                backgroundColor: getDayColumnBackground(theme, true, false),
                            }}
                        />
                        <Typography variant="caption">Public holiday</Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.5}>
                        <Box
                            sx={{
                                width: 12,
                                height: 12,
                                borderRadius: 0.5,
                                border: '1px dashed',
                                borderColor: 'divider',
                                opacity: 0.6,
                            }}
                        />
                        <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                            Default (unsaved)
                        </Typography>
                    </Box>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {loading ? (
                    <Box display="flex" justifyContent="center" p={4}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer component={Paper}>
                        <Table size="small" aria-label="timesheet grid">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Employee</TableCell>
                                    {days.map((day) => (
                                        <TableCell
                                            key={day}
                                            align="center"
                                            data-testid={`day-header-${day}`}
                                            sx={getDayColumnSx(theme, day, todayStr, holidaySet)}
                                        >
                                            <Typography variant="caption" component="div" sx={{ fontWeight: 'inherit' }}>
                                                {getDayName(day)}
                                            </Typography>
                                            {day.slice(5)}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredEmployees.map((emp) => (
                                    <TableRow key={emp.employee_id} hover>
                                        <TableCell>{emp.full_name}</TableCell>
                                        {days.map((day) => {
                                            const outOfEmploymentWindow =
                                                (!!emp.join_date && day < emp.join_date) ||
                                                (!!emp.termination_date && day > emp.termination_date);

                                            if (outOfEmploymentWindow) {
                                                // Before join_date/after termination_date: buildEffectiveEntries
                                                // (backend) never emits an entry for this cell, and any code saved
                                                // here would always fail server-side validation — so it's rendered
                                                // as a plain, non-interactive cell instead of a dead-end dropdown.
                                                return (
                                                    <TableCell
                                                        key={day}
                                                        align="center"
                                                        data-testid={`cell-${emp.employee_id}-${day}`}
                                                        data-out-of-window="true"
                                                        sx={{
                                                            color: 'text.disabled',
                                                            ...getDayColumnSx(theme, day, todayStr, holidaySet),
                                                        }}
                                                    >
                                                        —
                                                    </TableCell>
                                                );
                                            }

                                            const entry = entryMap.get(`${emp.employee_id}|${day}`);
                                            const isDefault = entry?.is_default === true;
                                            const codeColorSx = entry?.code ? getCodeColorSx(entry.code, theme.palette.mode) : undefined;
                                            return (
                                                <TableCell
                                                    key={day}
                                                    align="center"
                                                    data-testid={`cell-${emp.employee_id}-${day}`}
                                                    data-is-default={isDefault ? 'true' : 'false'}
                                                    sx={getDayColumnSx(theme, day, todayStr, holidaySet)}
                                                >
                                                    <Box display="flex" alignItems="center" justifyContent="center" gap={0.25}>
                                                        <Select
                                                            variant="standard"
                                                            value={entry?.code ?? ''}
                                                            displayEmpty
                                                            inputProps={{ 'aria-label': `${emp.full_name} ${day}` }}
                                                            onChange={(e) => handleCellChange(emp.employee_id, day, e.target.value as TimesheetCode)}
                                                            sx={{
                                                                minWidth: 56,
                                                                fontSize: '0.8rem',
                                                                ...(codeColorSx ?? {}),
                                                                ...(isDefault ? { fontStyle: 'italic', opacity: 0.6 } : {}),
                                                            }}
                                                        >
                                                            {/* No empty "—" option: every in-window cell always carries
                                                                either an HR-saved value or a generated default (see
                                                                buildEffectiveEntries), so an empty selection can never be
                                                                saved — the backend rejects code: '' with a 400. */}
                                                            {TIMESHEET_CODES.map((code) => (
                                                                <MenuItem
                                                                    key={code}
                                                                    value={code}
                                                                    sx={getCodeColorSx(code, theme.palette.mode)}
                                                                >
                                                                    {code}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                        {/* Only shown when this cell carries an HR-saved override
                                                            (not a generated default) — resetting a default is a no-op,
                                                            so there is nothing to reset. */}
                                                        {!isDefault && (
                                                            <IconButton
                                                                aria-label={`Reset ${emp.full_name} ${day} to default`}
                                                                size="small"
                                                                onClick={() => handleCellReset(emp.employee_id, day)}
                                                            >
                                                                <RestartAltIcon fontSize="inherit" />
                                                            </IconButton>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                ))}
                                {employees.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={days.length + 1} align="center">
                                            No active employees found.
                                        </TableCell>
                                    </TableRow>
                                )}
                                {employees.length > 0 && filteredEmployees.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={days.length + 1} align="center">
                                            No employees match your search.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </CardContent>

            <ConfirmOldEditDialog
                open={pendingEdit !== null}
                workDate={pendingEdit?.workDate ?? ''}
                onCancel={() => setPendingEdit(null)}
                onConfirm={() => {
                    if (pendingEdit) {
                        persistEdit(pendingEdit.employeeId, pendingEdit.workDate, pendingEdit.code, 'confirm');
                    }
                }}
            />

            <ConfirmOldEditDialog
                open={pendingReset !== null}
                workDate={pendingReset?.workDate ?? ''}
                onCancel={() => setPendingReset(null)}
                onConfirm={() => {
                    if (pendingReset) {
                        persistReset(pendingReset.employeeId, pendingReset.workDate, 'confirm');
                    }
                }}
                title="Confirm reset of an older entry"
                actionDescription="this reset"
                confirmLabel="Reset"
            />
        </Card>
    );
};
